import React, { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, Save, Copy, Search, Sofa, ExternalLink, Check, Wand2 } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

const FurniLatestPanel = ({ token, onOpenInPromo }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(50);
  const [savedMap, setSavedMap] = useState({}); // classname -> uploaded url
  const [savingId, setSavingId] = useState(null);

  // Same-Origin Render-Proxy (volle Originalgroesse). Fallback intern auf Icon.
  const renderUrl = (cn) => `${apiBase}/api/furniture/render-proxy/${encodeURIComponent(cn)}`;

  const load = async () => {
    setLoading(true);
    try {
      const r = await a.latestFurniture(limit);
      setItems(r.items || []);
    } catch (e) {
      toast({ title: "Konnte neue Moebel nicht laden", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [limit]);

  const onRefresh = async () => {
    setRefreshing(true);
    try { await load(); } finally { setRefreshing(false); }
  };

  const saveIcon = async (it) => {
    setSavingId(it.classname);
    try {
      const r = await a.saveFurnitureIcon(it.classname);
      const fullUrl = `${apiBase}${r.url}`;
      setSavedMap((p) => ({ ...p, [it.classname]: fullUrl }));
      toast({ title: "Gespeichert", description: `Im Medien-Bibliothek + Promo-Designer verfuegbar` });
      return { url: r.url, name: it.name || it.classname };
    } catch (e) {
      toast({ title: "Speichern fehlgeschlagen", description: e?.response?.data?.detail || String(e), variant: "destructive" });
      return null;
    } finally {
      setSavingId(null);
    }
  };

  const openInPromo = async (it) => {
    // Wenn noch nicht gespeichert: erst hochladen, dann via sessionStorage
    // dem PromoDesigner uebergeben + Tab-Wechsel ueber Callback.
    let payload = savedMap[it.classname] ? { url: savedMap[it.classname], name: it.name || it.classname } : null;
    if (!payload) {
      const res = await saveIcon(it);
      if (!res) return;
      payload = { url: `${apiBase}${res.url}`, name: res.name };
    }
    try {
      sessionStorage.setItem("promo_pending_overlay", JSON.stringify(payload));
    } catch { /* sessionStorage disabled - kein Pending */ }
    onOpenInPromo?.();
  };

  const copyUrl = async (it) => {
    const url = savedMap[it.classname];
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "URL kopiert", description: "Direkt als News-Header oder im Editor einsetzbar" });
    } catch {
      toast({ title: "Clipboard nicht verfuegbar", variant: "destructive" });
    }
  };

  const filtered = items.filter((it) => {
    if (!q.trim()) return true;
    const qq = q.toLowerCase();
    return (it.classname || "").toLowerCase().includes(qq)
      || (it.name || "").toLowerCase().includes(qq)
      || (it.description || "").toLowerCase().includes(qq);
  });

  return (
    <div className="space-y-4" data-testid="furni-latest-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Neue Moebel</h2>
          <p className="text-xs text-zinc-400 mt-1">
            Frische Habbo-Uploads in Originalgroesse (gerendert von habboapi.site, Fallback: habboassets-Icon).
            Klick &quot;In Medien speichern&quot; oder &quot;Im Promo-Designer oeffnen&quot; → das Moebel landet direkt im Editor.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            className="rounded-md border border-white/10 bg-[#10141b] px-2 py-1.5 text-xs text-zinc-200"
            data-testid="furni-latest-limit"
          >
            {[20, 50, 80, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <Button onClick={onRefresh} variant="ghost" className="text-zinc-300 hover:text-white" disabled={refreshing} data-testid="furni-latest-refresh">
            <RefreshCw className={`mr-1 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "..." : "Neu laden"}
          </Button>
        </div>
      </div>

      <div className="flex gap-2 rounded-xl border border-white/10 bg-[#0f141b] p-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Filter: Name, Classname, Beschreibung..."
            className="pl-9 bg-[#10141b] border-white/10 text-zinc-200" data-testid="furni-latest-search" />
        </div>
        <div className="text-xs text-zinc-500 flex items-center px-2">
          {filtered.length} von {items.length}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-[#0f141b] py-16 text-center text-sm text-zinc-500">
          <Sofa className="mx-auto h-10 w-10 text-zinc-700 mb-2" />
          Keine Moebel gefunden.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5" data-testid="furni-latest-grid">
          {filtered.map((it) => {
            const saved = savedMap[it.classname];
            const isSaving = savingId === it.classname;
            return (
              <div key={it.classname}
                className={`group relative flex flex-col rounded-lg border bg-[#0f141b] hover:border-[#7c4dff]/60 transition-all ${it.is_new ? "border-emerald-500/40" : "border-white/10"}`}
                data-testid={`furni-latest-tile-${it.classname}`}>
                {it.is_new && (
                  <span
                    className="pointer-events-none absolute -top-1.5 -right-1.5 z-10 inline-flex items-center gap-0.5 rounded-md border border-emerald-400/60 bg-gradient-to-br from-emerald-500 to-teal-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white shadow-lg shadow-emerald-500/40"
                    title={`Neu entdeckt: ${it.first_seen_at || ""}`}
                    data-testid={`furni-new-${it.classname}`}
                  >
                    Neu
                  </span>
                )}
                <div className="flex h-48 items-center justify-center bg-[#0a0d12] rounded-t-lg border-b border-white/5 p-2">
                  <img
                    src={renderUrl(it.classname)}
                    alt={it.classname}
                    className="max-h-44 max-w-full object-contain"
                    style={{ imageRendering: "pixelated" }}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                  />
                </div>
                <div className="flex-1 p-2 space-y-1">
                  <div className="text-xs font-semibold text-white line-clamp-1" title={it.name || it.classname}>
                    {it.name || <span className="text-zinc-500 italic">— ohne Namen —</span>}
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500 line-clamp-1" title={it.classname}>
                    {it.classname}
                  </div>
                  {it.description && (
                    <div className="text-[10px] text-zinc-400 line-clamp-2" title={it.description}>
                      {it.description}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 border-t border-white/5 p-2">
                  <div className="flex gap-1">
                    {saved ? (
                      <>
                        <Button
                          onClick={() => copyUrl(it)}
                          className="flex-1 btn-primary text-[11px] py-1 h-auto"
                          data-testid={`furni-latest-copy-${it.classname}`}
                        >
                          <Copy className="mr-1 h-3 w-3" /> URL kopieren
                        </Button>
                        <a
                          href={saved}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center rounded-md border border-white/10 px-2 hover:border-white/30 text-zinc-300"
                          title="Geoeffnetes Bild anzeigen"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </>
                    ) : (
                      <Button
                        onClick={() => saveIcon(it)}
                        disabled={isSaving}
                        className="flex-1 btn-ghost text-[11px] py-1 h-auto"
                        data-testid={`furni-latest-save-${it.classname}`}
                      >
                        {isSaving
                          ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          : <Save className="mr-1 h-3 w-3" />}
                        {isSaving ? "Speichert..." : "In Medien speichern"}
                      </Button>
                    )}
                  </div>
                  <Button
                    onClick={() => openInPromo(it)}
                    disabled={isSaving}
                    className="w-full bg-[#1f1830] hover:bg-[#2c2348] border border-[#7c4dff]/40 text-[#d4c7ff] text-[11px] py-1 h-auto"
                    data-testid={`furni-latest-open-promo-${it.classname}`}
                  >
                    <Wand2 className="mr-1 h-3 w-3" /> Im Promo-Designer oeffnen
                  </Button>
                </div>
                {saved && (
                  <div className="flex items-center gap-1 px-2 pb-2 text-[10px] text-emerald-400">
                    <Check className="h-3 w-3" /> Gespeichert
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FurniLatestPanel;
